import { serializeError } from 'serialize-error'
import { ExternalEvent } from '../AppContext'
import { SystemResourceUsageStats } from '../functionalities/SystemStats/SystemStats'
import { arrayInPlaceRemove } from '../utils'
import { env, setBrowserSessionEnvironmentVariables } from './EnvironmentVariables'

export const {
    ipcRenderer: ipc,
    shell,
    webUtils: _webUtils,
} = (() => {
    try {
        env.MUSE_RESOURCES_PATH_PROTOCOL = 'file://'

        // Why we need 'window.require': refer to
        //   <https://github.com/electron/electron/issues/7300>
        // need to set 'contextIsolation: false' when creating BrowserWindow.
        const r = window.require('electron')

        env.IS_ELECTRON = true

        return r
    } catch (e) {
        setBrowserSessionEnvironmentVariables()

        return {
            ipcRenderer: {
                send: () => {},
                once: () => {},
                removeListener: () => {},
                on: () => {},
                postMessage: () => {},
            },
            // @ts-ignore
            shell: {
                openExternal: () => {},
            },
            webUtils: {
                getPathForFile: (file: File) => {
                    return file.name
                },
            },
        }
    }
})()

export const webUtils = _webUtils

export interface WindowMetrics {
    x: number
    y: number
    width: number
    height: number
    isMaximized: boolean
    isFullScreen: boolean
}

function getEnvironmentVariables(): Promise<{ [key: string]: string }> {
    if (env.IS_ELECTRON) {
        return new Promise((resolve, reject) => {
            ipc.send('get-process-env')
            ipc.once('process-env', (event: any, message: string) => {
                resolve(JSON.parse(message))
            })
        })
    } else {
        return new Promise((resolve, reject) => {
            resolve({})
        })
    }
}

function setEnvironmentVariables(env: { [key: string]: string }): Promise<void> {
    return new Promise((resolve, reject) => {
        ipc.send('set-process-env', env)
        resolve()
    })
}

function getPath(type: string): Promise<string> {
    return new Promise((resolve, reject) => {
        ipc.send('app.getPath', type)
        ipc.once('main', (event: any, message: any) => {
            console.log(`ipc response: messages=%O`, message)
            resolve(message)
        })
    })
}

/**
 * Check if a directory exists and is writable.
 */
export type CheckDirectoryResult = {
    success: boolean
    code: string
}
function checkDirectory(
    directory: string,
    options?: { checkWritable?: boolean; createIfNotExists?: boolean }
): Promise<CheckDirectoryResult> {
    return new Promise((resolve, reject) => {
        if (!options) {
            options = {}
        }

        ipc.send('check-directory', { directory, options })
        ipc.once('check-directory-result', (event: any, message: CheckDirectoryResult) => {
            resolve(message)
        })
    })
}

function getOS(): Promise<string> {
    return new Promise((resolve, reject) => {
        ipc.send('get-os')
        ipc.once('os', (event: any, message: any) => {
            resolve(message)
        })
    })
}

function getAppVersion(): Promise<string> {
    return new Promise((resolve, reject) => {
        ipc.send('get-app-version')
        ipc.once('app-version', (event: any, message: any) => {
            resolve(message)
        })
    })
}

function getWindow(): Promise<WindowMetrics> {
    return new Promise((resolve, reject) => {
        ipc.send('get-window')
        ipc.once('window', (event: any, message: WindowMetrics) => {
            console.log(`ipc response: message=%O`, message)
            resolve(message)
        })
    })
}

type ElectronDisplayWithTrueBounds = Electron.Display & {
    nonScaledBounds: { x: number; y: number; width: number; height: number } | null
}
function getAllDisplays(): Promise<ElectronDisplayWithTrueBounds[]> {
    return new Promise((resolve, reject) => {
        ipc.send('get-all-displays')
        ipc.once('get-all-displays-result', (event: any, data: ElectronDisplayWithTrueBounds[]) => {
            resolve(data)
        })
    })
}

/**
 * Gets the display where the mainWindow resides in.
 */
function getFocusedDisplay(windowName?: string): Promise<Electron.Display | null> {
    return new Promise((resolve, reject) => {
        ipc.send('get-focused-display', windowName)
        ipc.once('get-focused-display-result', (event: any, data: Electron.Display | null) => {
            resolve(data)
        })
    })
}

function getDesktopCapturerSources(options: Electron.SourcesOptions): Promise<Electron.DesktopCapturerSource[]> {
    return new Promise((resolve, reject) => {
        ipc.send('get-desktop-capturer-sources', options)
        ipc.once('get-desktop-capturer-sources-result', (event: any, result: any) => {
            resolve(result || [])
        })
    })
}

interface IPCFileStats {
    size: number
}
function getFileStats(path: string): Promise<IPCFileStats> {
    return new Promise((resolve, _) => {
        ipc.send('get-file-stats', path)
        ipc.once('onfilestats', (event: any, stats: IPCFileStats) => {
            resolve(stats)
        })
    })
}

function open(p: { command: string; args?: object }): Promise<void> {
    return new Promise((resolve, _) => {
        ipc.send('open', p)
    })
}

function openLinkInBrowser(link: string): void {
    if (env.IS_ELECTRON) {
        shell.openExternal(link)
    } else {
        window.open(link, '_blank')
    }
}

function openPath(path: string): Promise<void> {
    return new Promise((resolve, _) => {
        ipc.send('open-path', path)
    })
}

function readFile(path: string, options?: any): Promise<any> {
    return new Promise((resolve, _) => {
        if (!options) {
            options = { encoding: 'utf8' }
        }
        ipc.send('read-file', { path, options })
        ipc.once('onreadfile', (event: any, data: any) => {
            resolve(data)
        })
    })
}

function deleteFile(path: string): Promise<any> {
    return new Promise((resolve, _) => {
        ipc.send('delete-file', path)
        ipc.once('ondeletefile', (event: any, result: any) => {
            resolve(result)
        })
    })
}

function readdir(path: string): Promise<NodeJS.ErrnoException | string[]> {
    return new Promise((resolve, _) => {
        ipc.send('readdir', path)
        ipc.once('readdir-result', (event: any, result: NodeJS.ErrnoException | string[]) => {
            resolve(result)
        })
    })
}

function reload(): Promise<void> {
    return new Promise((resolve, _) => {
        ipc.send('reload')
        resolve()
    })
}

function downloadFile(
    url: string,
    localCachePath: string | null,
    actionAfter: 'none' | 'open' | 'show-in-folder',
    downloadDirectory?: string
) {
    console.log(`sending ipc download-file: url=${url}`)
    ipc.send('download-file', { url, downloadDirectory, localCachePath, actionAfter })
}

type MediaAccessStatusCodes = 'not-determined' | 'granted' | 'denied' | 'restricted' | 'unknown'
function getMediaAccessStatus(
    mediaType: 'microphone' | 'camera' | 'screen' | 'accessibility'
): Promise<MediaAccessStatusCodes> {
    return new Promise((resolve) => {
        if (env.IS_ELECTRON) {
            ipc.send('get-media-access-status', { mediaType })
            ipc.once('status', (event: any, status: MediaAccessStatusCodes) => {
                resolve(status)
            })
        } else {
            // TODO: for now, just return true.
            resolve('granted')
        }
    })
}

function askForMediaAccess(mediaType: 'microphone' | 'camera' | 'screen' | 'accessibility'): Promise<void> {
    return new Promise((resolve) => {
        ipc.send('ask-for-media-access', { mediaType })
        resolve()
    })
}

function registerExternalEventListener(handler: (event: any, data: ExternalEvent) => void) {
    ipc.send('register-external-event')
    ipc.on('external-event', handler)
}

function removeExternalEventListener(handler: (event: any, data: ExternalEvent) => void) {
    ipc.removeListener('external-event', handler)
}

export type IpcWindowChangedEventData = {
    windowName: string
    eventName: string
    windowMetrics: WindowMetrics
}
export type IpcWindowChangedHandler = (data: IpcWindowChangedEventData) => void
let _windowChangedHandlersList: IpcWindowChangedHandler[] = []
function registerWindowChangedHandler(handler: IpcWindowChangedHandler) {
    console.log(`registerWindowChangedHandler: registering handler=%O`, handler)
    _windowChangedHandlersList.push(handler)

    console.log(`registerWindowChangedHandler: _windowChangedHandlersList=%O`, _windowChangedHandlersList)
}

ipc.send('register-window-change', { windowName: 'mainWindow' })
ipc.on('window-changed', (event: any, data: IpcWindowChangedEventData) => {
    _windowChangedHandlersList.forEach((h) => h(data))
})

function removeWindowChangedHandler(handler: any) {
    console.log(`removeWindowChangedHandler: removing handler=%O`, handler)
    arrayInPlaceRemove(_windowChangedHandlersList, (h) => h === handler)
    console.log(`removeWindowChangedHandler: _windowChangedHandlersList=%O`, _windowChangedHandlersList)
}

export interface DownloadProgressInfo {
    url: string
    transferredBytes: number
    isDone: boolean
    state: 'cancelled' | 'interrupted' | 'completed'
    savePath: string
}
function registerDownloadProgressHandler(handler: (event: any, info: DownloadProgressInfo) => void) {
    console.log(`IPC: registering download progress handler.`)
    ipc.on('download-progress', handler)
}

function removeDownloadProgressHandler(handler: any) {
    console.log(`IPC: deregistering download progress handler.`)
    ipc.removeListener('download-progress', handler)
}

export type IpcGetWindowStateResponse = {
    isFocused: boolean
}
function getWindowState(params: { windowName?: string }): Promise<IpcGetWindowStateResponse | null> {
    return new Promise((resolve) => {
        ipc.send('get-window-state', params)
        ipc.once('get-window-state', (event: any, data: IpcGetWindowStateResponse) => {
            try {
                resolve(data)
            } catch (e) {
                resolve(null)
            }
        })
    })
}

function setWindowBounds(params: {
    windowName?: string
    x?: number
    y?: number
    width?: number
    height?: number
    constraintX?: 'right' | 'left' | 'center'
    constraintY?: 'top' | 'bottom' | 'center'
    center?: boolean
    centerX?: boolean
    centerY?: boolean
    isMaximized?: boolean
    isFullscreen?: boolean
    allowSmallWidth?: boolean
    allowSmallHeight?: boolean
    allowNegativeX?: boolean
    allowNegativeY?: boolean
    noScreenBoundsCheck?: boolean
}) {
    ipc.send('set-window-bounds', params)
}

function setWindowOptions(params: {
    windowName?: string
    isAlwaysOnTop?: boolean
    minimize?: boolean
    toggleMaximizeRestore?: boolean
    isContentProtectionEnabled?: boolean
    triggerFocus?: boolean
    isTitleBarDragEnabled?: boolean
    show?: boolean
    close?: boolean
}) {
    ipc.send('set-window-options', params)
}

function showNotification(params: { title: string; body: string }) {
    ipc.send('show-notification', params)
}

function fileDragStart(params: { filePath: string }) {
    console.log(`at ElectronIPC fileDragStart: filePath=${params.filePath}`)
    ipc.send('file-drag-start', params)
}

function writeAudioFileChunk(params: {
    notifyFirstChunkArrived?: boolean
    isFirstChunk?: boolean
    uint8Array?: Uint8Array
    stop?: boolean
    filePath?: string | null // If specified, additionally saves to the specified file path.
    chunkIndex?: number
}) {
    ipc.send('write-audio-file-chunk', params)
}

async function saveAudioFileChunk(params: { filePath: string; isFirstChunk?: boolean; uint8Array?: Uint8Array }) {
    return new Promise((resolve, reject) => {
        ipc.send('save-audio-file-chunk', params)
        let timeout = setTimeout(() => {
            reject({ code: 'timeout' })
        }, 3000)

        ipc.once('save-audio-file-chunk-result', (event: any, result: any) => {
            clearTimeout(timeout)
            if (result?.success === true) {
                resolve(true)
            } else {
                reject(result?.error || {})
            }
        })
    })
}

function openProAudioFileRecordingPort(): MessagePort {
    const { port1: chromePort, port2: electronPort } = new MessageChannel()

    ipc.postMessage('open-pro-audio-file-recording-port', {}, [electronPort])

    return chromePort
}

function sendDAWControlCommand(params: { command: 'start-recording' | 'stop-recording' }) {
    ipc.send('send-daw-control-command', params)
}

function midiDataOut(params: { data: number[] }) {
    ipc.send('midi-data-out', params)
}

function openMacSystemPreferences(params: { pane: string; section: string }) {
    ipc.send('open-mac-system-preferences', params)
}

export interface FileDialogResult {
    paths: string[] | null
}
function openFileDialog(options: { [key: string]: any }): Promise<FileDialogResult> {
    return new Promise((resolve) => {
        ipc.send('open-file-dialog', options)
        ipc.once('open-file-dialog-result', (event: any, obj: FileDialogResult) => {
            resolve(obj)
        })
    })
}

function openDevTools(): Promise<void> {
    return new Promise((resolve) => {
        ipc.send('open-dev-tools')
    })
}

function robotJSCommand(functionName: string, params: any[]) {
    return new Promise((resolve) => {
        if (env.LOG_REMOTE_DESKTOP_CONTROL) {
            console.log(`LOG_REMOTE_DESKTOP_CONTROL: robotJSCommand: ${functionName}(` + JSON.stringify(params) + `)`)
        }

        ipc.send('robot-js-command', { functionName, params })

        if (functionName === 'getMousePos') {
            ipc.once('robotjs-result', (event: any, data: any) => {
                resolve(data)
            })
        }
    })
}

function registerSystemDeviceVolumeHandler(params: {
    handler: (event: any, volumes: { [key: string]: number }) => void
}) {
    ipc.on('system-device-volume', params.handler)
}

function removeSystemDeviceVolumeHandler(params: {
    handler: (event: any, volumes: { [key: string]: number }) => void
}) {
    ipc.removeListener('system-device-volume', params.handler)
}

function getSystemDeviceVolume(params: { devices: { nativeDeviceId: string; isInput: boolean }[] }) {
    ipc.send('get-system-device-volume', params)
}

function getNativeDeviceMute(params: { devices: { nativeDeviceId: string; isInput: boolean }[] }) {
    ipc.send('get-native-device-mute', params)
}

function setSystemDeviceVolume(params: { nativeDeviceId: string; value: number; isInput: boolean }) {
    ipc.send('set-system-device-volume', params)
}

export interface NativeAudioDevice {
    label: string
    deviceId: number
    deviceUid: string
    isInput: boolean
    isOutput: boolean
    // below parameters defined for win32 NAB
    isDefaultInput?: boolean
    isDefaultOutput?: boolean
}
let getNativeAudioDevicesListTimer: NodeJS.Timeout | undefined = undefined
function getNativeAudioDevicesList(options?: { timeoutMs?: number }): Promise<NativeAudioDevice[]> {
    return new Promise((resolve, reject) => {
        const timeoutMs = options?.timeoutMs || 8000
        clearTimeout(getNativeAudioDevicesListTimer)

        console.log(`ElectronIPC/getNativeAudioDevicesList: ipc.send() 'get-audio-devices-list'.`)
        ipc.send('get-audio-devices-list')

        ipc.once('get-audio-devices-list-result', (event: any, message: string) => {
            clearTimeout(getNativeAudioDevicesListTimer)
            console.log(
                `ElectronIPC/getNativeAudioDevicesList: received 'get-audio-devices-list-result' ` + ` with message=%O`,
                message
            )
            if (typeof message === 'string') {
                try {
                    resolve(JSON.parse(message))
                } catch (e) {
                    console.error(
                        `ElectronIPC/getNativeAudioDevicesList: failed to parse message! ` + `message=%O e=%O`,
                        message,
                        serializeError(e)
                    )
                    resolve([])
                }
            } else if (Array.isArray(message)) {
                console.log(`ElectronIPC/getNativeAudioDevicesList: resolving %O`, message)
                resolve(message)
            } else {
                console.error(
                    `ElectronIPC/getNativeAudioDevicesList: resolving [] because message type is ` +
                        `neither string nor array; instead, it is "${typeof message}".`
                )
                resolve([])
            }
        })

        // response timeout timer:
        getNativeAudioDevicesListTimer = setTimeout(() => {
            console.error(
                `ElectronIPC/getNativeAudioDevicesList: ipc request timed out ` + `in ${timeoutMs}. returning [].`
            )
            resolve([])
        }, timeoutMs)
    })
}

function createMuseMultiOutputDevice(): Promise<{ success: boolean; message: string }> {
    return new Promise((resolve, reject) => {
        ipc.send('createMuseMultiOutputDevice')
        ipc.once('createMuseMultiOutputDevice:reply', (event: any, obj: string) => {
            try {
                resolve(JSON.parse(obj))
            } catch (e) {
                resolve({
                    success: false,
                    message: `ElectronIPC failed to parse JSON: "${obj}"`,
                })
            }
        })
    })
}

/**
 * Locks the MainWindow from closing.
 * When the user tries to close the window, the callback is invoked.
 */
let onWindowClosingCache: ((event: any, message: string) => void) | null | undefined = null
function lockMainWindowClosing(
    lock: boolean | null,
    onWindowClosing?: (event: any, message: string) => void
): Promise<boolean> {
    return new Promise((resolve, reject) => {
        if (typeof lock === 'boolean') {
            ipc.send('lock-main-window-closing', { lock })
        }

        if (lock === true) {
            onWindowClosingCache = onWindowClosing
            if (typeof onWindowClosing === 'function') {
                ipc.on('main-window-closing', onWindowClosing)
            }
        } else if (lock === false) {
            if (typeof onWindowClosing === 'function') {
                try {
                    ipc.removeListener('main-window-closing', onWindowClosing)
                } catch (e) {}
            }
            if (typeof onWindowClosingCache === 'function') {
                try {
                    ipc.removeListener('main-window-closing', onWindowClosingCache)
                } catch (e) {}
                onWindowClosingCache = null
            }
        } else {
            if (typeof onWindowClosing === 'function') {
                try {
                    ipc.removeListener('main-window-closing', onWindowClosing)
                } catch (e) {}
            }
        }
    })
}

function triggerGlOdevChanged() {
    ipc.send('trigger-gl-odev-changed')
}

function iohookSetActiveKeys(activeKeys: [number]) {
    ipc.send('iohook-set-active-keys', activeKeys)
}

function iohookSetMode(mode: 'normal' | 'listen') {
    ipc.send('iohook-set-mode', mode)
}

type RemoteRecordingPlayerData = {
    prerollDuration?: number
    bufferDuration?: number
    forwardCompensationMs?: number
    chunk?: Uint8Array
}
function remoteRecordingPlayer(params: { opcode: string; data: RemoteRecordingPlayerData }) {
    ipc.send('remote-recording-player', params)
}

let __setRemoteRecordingPlayerEventHandler: any = null
function setRemoteRecordingPlayerEventHandler(handler: (event: any, data: any) => void) {
    return new Promise(() => {
        if (typeof __setRemoteRecordingPlayerEventHandler === 'function') {
            ipc.removeListener('remote-recording-player-event', __setRemoteRecordingPlayerEventHandler)
        }

        __setRemoteRecordingPlayerEventHandler = handler
        ipc.on('remote-recording-player-event', __setRemoteRecordingPlayerEventHandler)
    })
}

function setGlobalAudioOuputDevice(params: { nativeDeviceId: string }) {
    ipc.send('set-global-audio-output-device', params)
}

function setMenu(params: { menuName: string }) {
    ipc.send('set-menu', params)
}

function exceptionTest(type: string) {
    ipc.send('exception-test', type)
}

export type FfmpegExecResult = {
    code: number
    message?: string
}
function ffmpegExec(args: string[], progressHandler?: () => void): Promise<FfmpegExecResult> {
    return new Promise((resolve, _) => {
        ipc.send('ffmpeg-exec', args)

        // TODO: proper handler registration and removal
        // ipc.on('ffmpeg-progress', data => {
        //     if (progressHandler) {
        //         progressHandler()
        //     }
        // })

        ipc.once('ffmpeg-done', (event: any, data: FfmpegExecResult) => {
            resolve(data)
        })
    })
}

export type ObsLaunchResult = {
    code:
        | 'obs_launch_success'
        | 'e_obs_launch_failed'
        | 'e_obs_not_available'
        | 'e_obs_proc_error'
        | 'e_obs_launch_timed_out'
    pid: number
}
function ObsInterface(instanceName: string, methodName: string, params: any[] | any): Promise<any> {
    return new Promise((resolve) => {
        console.log(`ElectronIPC: send 'ObsInterface' methodName=${methodName} params=` + JSON.stringify(params))
        ipc.send('ObsInterface', { instanceName, methodName, params })
        ipc.once(`ObsInterface.${methodName}.result`, (event: any, data: any) => {
            resolve(data)
        })
    })
}

type DawPluginMessage = { [key: string]: any }
function sendToDawPlugin(destination: string, msg: DawPluginMessage) {
    ipc.send('send-to-daw-plugin', { destination, msg })
}

function setPowerSaveBlocker(value: boolean) {
    ipc.send('set-power-save-blocker', { value })
}

function systemReboot() {
    ipc.send('system-reboot', {})
}

// function launchObs(args: string[]) {
//     return new Promise((resolve, _) => {
//         ipc.send('launch-obs', args)
//
//         // ipc.once('ffmpeg-done', (event: any, data: FfmpegExecResult) => {
//         //     resolve(data)
//         // })
//     })
// }

// function stopObs() {
//     return new Promise((resolve, _) => {
//         ipc.send('stop-obs')
//     })
// }

function writeToLogFileStream(str: string) {
    ipc.send('write-to-log-file-stream', str)
}

function getSystemResourceUsageStats(): Promise<SystemResourceUsageStats | null> {
    return new Promise((resolve) => {
        if (env.IS_ELECTRON) {
            ipc.send('get-system-resource-usage-stats')
            ipc.once(`get-system-resource-usage-stats-result`, (event: any, data: any) => {
                resolve(data)
            })
        } else {
            resolve(null)
        }
    })
}

function forceClosePresentationSidebar(data: { doClose: boolean }) {
    ipc.send('force-close-presentation-sidebar', data)
}

/**
 * A way for the renderer window to quit the entire app.
 */
function quitApp(options?: { immediate?: boolean; relaunch?: boolean }) {
    ipc.send('quit-app', options)
}

function showContextMenu(options: { cut?: boolean; copy?: boolean; paste?: boolean } = {}) {
    ipc.send('show-context-menu', options)
}

function copyToClipboard(text: string) {
    ipc.send('copy-to-clipboard', { text })
}

export default {
    getEnvironmentVariables,
    setEnvironmentVariables,
    getPath,
    checkDirectory,
    getOS,
    getAppVersion,
    getWindow,
    getAllDisplays,
    getFocusedDisplay,
    getDesktopCapturerSources,
    getFileStats,
    open,
    openLinkInBrowser,
    openPath,
    readFile,
    deleteFile,
    readdir,
    reload,
    downloadFile,
    getMediaAccessStatus,
    askForMediaAccess,
    registerExternalEventListener,
    removeExternalEventListener,
    registerWindowChangedHandler,
    removeWindowChangedHandler,
    registerDownloadProgressHandler,
    removeDownloadProgressHandler,
    getWindowState,
    setWindowBounds,
    setWindowOptions,
    showNotification,
    fileDragStart,
    writeAudioFileChunk,
    saveAudioFileChunk,
    openProAudioFileRecordingPort,
    sendDAWControlCommand,
    midiDataOut,
    openMacSystemPreferences,
    openFileDialog,
    openDevTools,
    robotJSCommand,
    registerSystemDeviceVolumeHandler,
    removeSystemDeviceVolumeHandler,
    getSystemDeviceVolume,
    getNativeDeviceMute,
    setSystemDeviceVolume,
    getNativeAudioDevicesList,
    createMuseMultiOutputDevice,
    lockMainWindowClosing,
    triggerGlOdevChanged,
    iohookSetActiveKeys,
    iohookSetMode,
    remoteRecordingPlayer,
    setRemoteRecordingPlayerEventHandler,
    setGlobalAudioOuputDevice,
    setMenu,
    exceptionTest,
    ffmpegExec,
    ObsInterface,
    writeToLogFileStream,
    getSystemResourceUsageStats,
    sendToDawPlugin,
    setPowerSaveBlocker,
    systemReboot,
    forceClosePresentationSidebar,
    quitApp,
    showContextMenu,
    copyToClipboard,
}
